import React from 'react';
import { Star, Quote, ChevronDown, Wrench, CalendarCheck, MapPin, Award, CheckCircle2, ArrowRight } from 'lucide-react';
import ServiceLandingHero from '../components/services/ServiceLandingHero';
import { ProcessSection, BenefitsSection, FAQSection } from './WerkstattReparaturPage';
import { HU_QUARTERS } from '../data/commonChoices';

import ServiceReviews, { FACES } from '../components/services/ServiceReviews';
import { usePageMeta } from '../lib/seo';
/**
 * TuevServicePage — TÜV-/HU-Service mit Prepaid-Tankkarten-Guthaben zahlbar.
 * Die Prepaid-Tankkarte lädt sich auf, sobald der Kunde ein FuelX-Hotel bucht.
 */

const TUEV_FIELDS = [
  { name: 'brand', label: 'Marke', type: 'brand', linkedModelKey: 'model', required: true },
  { name: 'model', label: 'Modell', type: 'model', linkedBrandKey: 'brand', required: true },
  { name: 'registration_year', label: 'Baujahr', type: 'year', required: true, colSpan: 2 },
  { name: 'hu_expires', label: 'HU-Fälligkeit', type: 'pills', columns: 4, required: true, options: HU_QUARTERS, hint: 'Steht auf deiner TÜV-Plakette.', colSpan: 2 },
  { name: 'has_ai_check', label: 'AU-Pflicht?', type: 'pills-grid', columns: 3, options: ['Ja', 'Nein', 'Weiß ich nicht'], required: true, colSpan: 2 },
  { name: 'has_fuelx_booking', label: 'Über FuelX Hotels gebucht?', type: 'pills-grid', columns: 3, options: ['Ja — bereits gebucht', 'Plane ich', 'Noch unklar'], required: true, colSpan: 2 },
  { name: 'preferred_workshop_city', postalKey: 'preferred_workshop_postal_code', label: 'PLZ + Wunsch-Stadt für den Termin', type: 'postal-city', required: true, colSpan: 2 },
  { name: 'preferred_window', label: 'Gewünschter Zeitraum', type: 'pills-grid', columns: 4, options: ['Diese Woche', 'In 2 Wochen', 'Innerhalb 1 Monat', 'Flexibel'], required: true, colSpan: 2 },
  { name: 'contact_name', label: 'Dein Name', placeholder: 'Vor- und Nachname', required: true },
  { name: 'contact_email', label: 'E-Mail', type: 'email', placeholder: 'du@beispiel.de', required: true },
  { name: 'postal_code', label: 'PLZ', placeholder: 'z. B. 10115', required: true, maxLength: 5 },
  { name: 'contact_phone', label: 'Telefon', type: 'tel', placeholder: '+49 …', required: true },
];

const TUEV_STEPS = [
  { title: 'Fahrzeug', description: 'Marke, Modell, Baujahr.', fields: ['brand', 'model', 'registration_year'] },
  { title: 'TÜV-Daten', description: 'Wann läuft die HU ab? AU-pflichtig?', fields: ['hu_expires', 'has_ai_check', 'has_fuelx_booking'] },
  { title: 'Termin', description: 'Wo und wann?', fields: ['preferred_workshop_city', 'preferred_window'] },
  { title: 'Kontakt', description: 'Damit wir dir den Termin bestätigen können.', fields: ['contact_name', 'contact_email', 'postal_code', 'contact_phone'] },
];

const BENEFITS = [
  { icon: Wrench,         title: 'Prepaid-Tankkarte deckt den TÜV', desc: 'HU, AU, Kontrollgebühr, Dienstleister-Werkstatt — alles direkt aus deinem Guthaben abrechenbar, ohne Vorkasse.' },
  { icon: MapPin,         title: '2.400+ Dienstleister-Werkstätten', desc: 'Dichtes Netzwerk in DE, AT, CH. Terminvereinbarung direkt bei dir um die Ecke.' },
  { icon: CalendarCheck,  title: 'Wir organisieren alles', desc: 'Terminvereinbarung, Fahrzeugabholung auf Wunsch, Reifen-/Bremsen-Check inklusive.' },
  { icon: Award,          title: 'TÜV Süd / Dekra / GTÜ', desc: 'Nur zertifizierte Prüf-Organisationen — keine Billig-Werkstätten.' },
];

const PROCESS = [
  { n: '01', title: 'FuelX-Hotel buchen',          desc: 'Buche deine nächste FuelX-Hotel-Reise — deine Prepaid-Tankkarte wird automatisch aufgeladen.' },
  { n: '02', title: 'TÜV-Anfrage einreichen',       desc: 'Formular ausfüllen — wir organisieren den HU-Termin bei einem Dienstleister in deiner Nähe und senden dir ein persönliches Angebot.' },
  { n: '03', title: 'Aus Prepaid-Tankkarte zahlen', desc: 'Du akzeptierst das Angebot und zahlst aus deiner Prepaid-Tankkarte. Bei fehlendem Guthaben ergänzt du via Stripe.' },
];

const FAQS = [
  { q: 'Wie oft kann ich den TÜV-Service nutzen?', a: 'Einmal alle 24 Monate — also genau im HU-Rhythmus. Für jedes Fahrzeug, das auf dich oder deinen FuelX-Account zugelassen ist.' },
  { q: 'Wie wird der TÜV abgerechnet?', a: 'Nach deiner Anfrage erhältst du ein persönliches Angebot. Du zahlst komplett aus deiner Prepaid-Tankkarte (aufgeladen durch FuelX-Hotelbuchungen) — ergänzt bei Bedarf via Stripe.' },
  { q: 'Was, wenn mein Auto die HU nicht besteht?', a: 'Wir erstellen einen Mängelbericht und vermitteln dich an unsere Dienstleister-Werkstätten für die Mängelbehebung. Die Nachprüfung wird ebenfalls über dein Prepaid-Guthaben verrechnet.' },
  { q: 'Muss ich eine bestimmte FuelX-Karte haben?', a: 'Jede FuelX-Karte (Silver oder Gold) schaltet den TÜV-Service frei. Voraussetzung: ausreichendes Guthaben auf deiner Card.' },
  { q: 'Kann ich auch eine AU zusammen mit der HU machen?', a: 'Ja — die Abgasuntersuchung ist im TÜV-Service enthalten. Kein separater Termin, kein Mehraufwand.' },
];

export default function TuevServicePage() {
  usePageMeta('TÜV-Kosten zurückholen — bis zu 160 € Vorteil | FuelX', 'HU/AU-Termin in deiner Nähe — bis zu 160 € TÜV/HU-Kosten nach den geltenden Bedingungen über FuelGood-Guthaben zurückholen. Jetzt Termin anfragen.');
  return (
    <div className="min-h-screen bg-white">
      <ServiceLandingHero
        image="/tuev-flotte-kostenblock.webp"
        imageAlt="TÜV für ein Auto? Für eine Flotte ein Kostenblock. Bis zu 160 € Vorteil je berechtigtem Fahrzeug."
        serviceKey="tuev"
        eyebrow="TÜV-Service · Prepaid-Tankkarte"
        line1="TÜV & HU."
        line2="Zahlt dein Guthaben."
        sub="Der TÜV ist Pflicht — aussuchen kannst du dir nur, wer ihn bezahlt. Ab jetzt: dein FuelGood Guthaben. Mit einer qualifizierenden FuelX-Hotels-Buchung gehst du raus mit bis zu 160 € HU/AU-Kosten zurück auf deiner FuelX Prepaid Card."
        statValues={[{v:'2.400+', l:'Dienstleister-Werkstätten'}, {v:'0 €', l:'Servicegebühr'}, {v:'TÜV · Dekra', l:'· GTÜ'}, {v:'< 60 Min.', l:'Dauer'}]}
        endpoint="/service-inquiries/tuev"
        fields={TUEV_FIELDS}
        steps={TUEV_STEPS}
        submitLabel="Termin anfragen"
        testId="tuev-form"
        formHeadline="TÜV-Termin anfragen."
        formSubhead="Fahrzeug + HU-Fälligkeit + Stadt — wir kümmern uns schnellstmöglich um deinen Termin."
      />

      {/* Problem → Lösung: Editorial-Sektion mit dem zweiten Motiv */}
      <section className="py-16 sm:py-24 bg-white" data-testid="tuev-problem-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative overflow-hidden aspect-[16/9] -mx-6 sm:mx-0 sm:rounded-3xl border-y sm:border border-stone-100 sm:shadow-sm mb-10 sm:mb-14">
            <img
              src="/tuev/tuev-problem.jpg"
              alt="Alle 2 Jahre. Immer wieder zahlen. — HU & AU kosten 2026 im Schnitt rund 163 €"
              className="w-full h-full object-cover"
              loading="lazy"
              data-testid="tuev-problem-image"
            />
          </div>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-stone-400 mb-4">Die HU-Rechnung, die immer wiederkommt</p>
              <h2 className="font-display text-stone-900 leading-[0.98] tracking-[-0.028em] text-3xl sm:text-4xl lg:text-[2.75rem] mb-4">
                163 € — alle zwei Jahre.<br />
                <span className="text-emerald-600">Ab jetzt zahlt dein Guthaben.</span>
              </h2>
              <p className="text-stone-500 text-sm sm:text-base leading-relaxed max-w-lg">
                Alle 24 Monate ruft die HU — rund 163 € pro Fahrzeug, nicht verhandelbar. Mit FuelX
                kannst du dir bis zu 160 € HU/AU-Kosten auf deine FuelX Prepaid Card gutschreiben lassen.
              </p>
            </div>
            <div className="lg:pt-2">
              <ul className="space-y-3 mb-8">
                {[
                  'HU/AU-Kosten: bis zu 160 € auf FuelX Prepaid Card gutschreibbar',
                  'Termin, Dienstleister-Werkstatt & Abwicklung — FuelX organisiert alles',
                  'Nur zertifizierte Prüforganisationen: TÜV Süd, Dekra, GTÜ',
                ].map((t, i) => (
                  <li key={t} className="flex items-start gap-2.5" data-testid={`tuev-problem-point-${i}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                    <span className="text-sm text-stone-700 leading-snug">{t}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#form"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-stone-800 transition-colors group"
                data-testid="tuev-problem-cta"
              >
                Jetzt TÜV-Termin anfragen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ProcessSection items={PROCESS} />
      <BenefitsSection benefits={BENEFITS} />

      <section className="py-16 sm:py-20 bg-white border-y border-stone-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />)}
            <span className="ml-2 text-sm font-medium text-stone-900">4,8 / 5 · TÜV-Service</span>
          </div>
          <Quote className="w-8 h-8 text-stone-300 mx-auto mb-4" />
          <blockquote className="font-display text-2xl sm:text-3xl text-stone-900 italic leading-snug max-w-3xl mx-auto mb-6">
            „In 48 h war der Termin gebucht. 60 Minuten später hatte ich die neue Plakette — komplett aus meiner Prepaid-Tankkarte bezahlt."
          </blockquote>
          <p className="text-sm text-stone-500">Julia S. · Hamburg · Audi A4 Avant, 2020</p>
        </div>
      </section>

      <ServiceReviews
        hideAvatar
        eyebrow="TÜV-Service · Verifizierte Kundenstimmen"
        title="Plakette geholt. Nichts draufgezahlt."
        rating="4,8"
        testId="tuev-reviews"
        reviews={[
          { name: 'Matthias L.', city: 'Köln', detail: 'BMW 3er Touring · HU + AU', img: FACES.matthias, text: 'Die HU war überfällig — im Familienalltag rutscht so etwas immer nach hinten. Anfrage abgeschickt, zwei Tage später stand der Termin. Bezahlt komplett aus der Prepaid-Tankkarte unserer Sommerreise: 0 € aus eigener Tasche.', saved: '112 € gedeckt' },
          { name: 'Franzi M.', city: 'München', detail: 'BMW 1er · HU', img: FACES.julia, text: 'Früher habe ich irgendeinen Prüftermin genommen und einfach bezahlt. Diesmal: Formular ausgefüllt, Angebot bekommen, Plakette geholt — und die Gebühr war durch unsere Städtereise schon komplett gedeckt.', saved: '89 € gedeckt' },
          { name: 'Peter W.', city: 'Nürnberg', detail: 'Mercedes E-Klasse · HU + Nachprüfung', img: FACES.peter, text: 'Beim Termin fiel eine verschlissene Bremse auf. FuelX hat Werkstatt und Nachprüfung direkt mitorganisiert — ein Ansprechpartner statt drei Anrufe. Die Nachprüfung lief ebenfalls über mein Prepaid-Guthaben.', saved: 'Alles aus 1 Hand' },
        ]}
      />
      <FAQSection faqs={FAQS} />
    </div>
  );
}
